export type UrlState = {
  projectId: string | null;
  shareToken: string | null;
  /** Backward-compat `?pub=<id>` (older share artifacts). */
  publicProjectId: string | null;
  /** Pretty-path `/p/<slug>` for public links. */
  publicSlug: string | null;
  /** Pretty-path `/u/<owner-slug>` for owner profile pages. */
  ownerSlug: string | null;
};

export const URL_STATE_CHANGE_EVENT = "crimp:url-state-change";

export function readUrlState(
  search: string = window.location.search,
  pathname: string = window.location.pathname,
): UrlState {
  const params = new URLSearchParams(search);
  let publicSlug: string | null = null;
  let ownerSlug: string | null = null;
  const pubMatch = pathname.match(/^\/p\/([^/?#]+)/i);
  if (pubMatch) publicSlug = decodeURIComponent(pubMatch[1]);
  const ownerMatch = pathname.match(/^\/u\/([^/?#]+)/i);
  if (ownerMatch) ownerSlug = decodeURIComponent(ownerMatch[1]);
  return {
    projectId: params.get("p"),
    shareToken: params.get("s"),
    publicProjectId: params.get("pub"),
    publicSlug,
    ownerSlug,
  };
}

/** True when this load arrived on a *shared* schematic — `?s=`, legacy `?pub=`,
 *  or a `/p/` slug, but never `/u/`, which renders no schematic. The display
 *  defaults in `settings/` read it once at module init. */
export function isSharedArrival(search?: string, pathname?: string): boolean {
  if (search === undefined || pathname === undefined) {
    if (typeof window === "undefined") return false;
    search ??= window.location.search;
    pathname ??= window.location.pathname;
  }
  const { shareToken, publicProjectId, publicSlug } = readUrlState(
    search,
    pathname,
  );
  return Boolean(shareToken || publicProjectId || publicSlug);
}

function replaceUrl(qs: string, path: string = "/") {
  const next = `${path}${qs ? `?${qs}` : ""}${window.location.hash}`;
  window.history.replaceState({}, "", next);
  window.dispatchEvent(new Event(URL_STATE_CHANGE_EVENT));
}

function basePath(): string {
  // The SPA always serves from "/", so drop any /p/ or /u/ segment.
  const p = window.location.pathname;
  if (/^\/(p|u)\//.test(p)) return "/";
  return p;
}

export function setProjectIdInUrl(projectId: string | null): void {
  const params = new URLSearchParams(window.location.search);
  if (projectId) {
    params.set("p", projectId);
  } else {
    params.delete("p");
  }
  // Both owned cloud projects and local projects leave the shared view.
  params.delete("s");
  params.delete("pub");
  replaceUrl(params.toString(), "/app");
}

export function setShareTokenInUrl(token: string | null): void {
  const params = new URLSearchParams(window.location.search);
  if (token) {
    params.set("s", token);
    params.delete("p");
    params.delete("pub");
  } else {
    params.delete("s");
  }
  replaceUrl(params.toString(), basePath());
}

/** Public project's pretty URL, falling back to `?pub=<id>` while a
 *  newly-published project's slug is still being minted. */
export function setPublicUrl(slug: string | null, projectId: string | null): void {
  if (slug) {
    const next = `/p/${encodeURIComponent(slug)}${window.location.hash}`;
    window.history.replaceState({}, "", next);
    return;
  }
  if (projectId) {
    const params = new URLSearchParams();
    params.set("pub", projectId);
    replaceUrl(params.toString());
    return;
  }
  replaceUrl("");
}

export function setOwnerUrl(ownerSlug: string): void {
  const next = `/u/${encodeURIComponent(ownerSlug)}${window.location.hash}`;
  window.history.replaceState({}, "", next);
}

export function buildShareUrl(token: string): string {
  const u = new URL(window.location.href);
  u.pathname = "/";
  u.search = `?s=${encodeURIComponent(token)}`;
  u.hash = "";
  return u.toString();
}

/** Pretty public URL, or `?pub=<id>` when the project has no slug yet. */
export function buildPublicUrl(slugOrId: string, opts: { slug?: boolean } = {}): string {
  const u = new URL(window.location.href);
  const useSlug = opts.slug ?? true;
  if (useSlug) {
    u.pathname = `/p/${encodeURIComponent(slugOrId)}`;
    u.search = "";
  } else {
    u.pathname = "/";
    u.search = `?pub=${encodeURIComponent(slugOrId)}`;
  }
  u.hash = "";
  return u.toString();
}

export function buildOwnerUrl(ownerSlug: string): string {
  const u = new URL(window.location.href);
  u.pathname = `/u/${encodeURIComponent(ownerSlug)}`;
  u.search = "";
  u.hash = "";
  return u.toString();
}

export function slugifyOwnerName(name: string): string {
  return (
    name
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 40) || "anonymous"
  );
}
